import React from 'react'
import { graphql } from 'gatsby'
import ChickenFingerCalculator, {
  CHICKEN_FINGER_CALCULATOR_CATEGORIES,
  ChickenFingerCalculatorBackgroundColor,
  ChickenFingerCalculatorCategory,
  ChickenFingerCalculatorContent,
  ChickenFingerCalculatorQuantityMode,
  ChickenFingerCalculatorRule,
} from 'src/components/organisms/engagement/chickenFingerCalculator'
import {
  PrismicPageDataBodyChickenFingerCalculator,
  PrismicPageDataBodyChickenFingerCalculatorItem,
} from 'src/typings/generated/graphql'

type Props = {
  slice: PrismicPageDataBodyChickenFingerCalculator
}

const isCategory = (
  category?: string | null
): category is ChickenFingerCalculatorCategory =>
  CHICKEN_FINGER_CALCULATOR_CATEGORIES.some(option => option === category)

const isQuantityMode = (
  quantityMode?: string | null
): quantityMode is ChickenFingerCalculatorQuantityMode =>
  Object.values(ChickenFingerCalculatorQuantityMode).some(
    option => option === quantityMode
  )

export const mapBackgroundColor = (
  backgroundColor?: string | null
): ChickenFingerCalculatorBackgroundColor => {
  const normalizedColor = backgroundColor?.toLowerCase()

  return Object.values(ChickenFingerCalculatorBackgroundColor).some(
    option => option === normalizedColor
  )
    ? (normalizedColor as ChickenFingerCalculatorBackgroundColor)
    : ChickenFingerCalculatorBackgroundColor.red
}

const mapRule = (
  item: PrismicPageDataBodyChickenFingerCalculatorItem
): ChickenFingerCalculatorRule | undefined => {
  if (
    !isCategory(item.category) ||
    !item.item_name ||
    !item.applicable_people_min ||
    !isQuantityMode(item.quantity_mode)
  ) {
    return undefined
  }

  return {
    category: item.category,
    itemName: item.item_name,
    itemDescription: item.item_description?.text ?? undefined,
    itemImage: item.item_image?.gatsbyImageData ?? undefined,
    itemImageAlt: item.item_image?.alt ?? undefined,
    applicablePeopleMin: item.applicable_people_min,
    applicablePeopleMax: item.applicable_people_max ?? undefined,
    servingCapacity: item.serving_capacity ?? undefined,
    quantityMode: item.quantity_mode,
    fixedQuantity: item.fixed_quantity ?? undefined,
    resultLabel: item.result_label ?? undefined,
  }
}

const ChickenFingerCalculatorSlice = ({ slice }: Props) => {
  const items = slice.items ?? []
  const content: ChickenFingerCalculatorContent = {
    anchor: slice.primary.anchor ?? undefined,
    backgroundColor: mapBackgroundColor(slice.primary.background_color),
    eyebrowText: slice.primary.eyebrow_text ?? undefined,
    headline: slice.primary.headline ?? undefined,
    description: slice.primary.description?.text ?? undefined,
    inputLabel: slice.primary.input_label ?? undefined,
    minimumPeople: slice.primary.minimum_people ?? undefined,
    maximumPeople: slice.primary.maximum_people ?? undefined,
    sliderIncrement: slice.primary.slider_increment ?? undefined,
    noResultsMessage: slice.primary.no_results_message ?? undefined,
    categoryDescriptions: {
      Tailgates: slice.primary.tailgates_description?.text ?? undefined,
      Combos: slice.primary.combos_description?.text ?? undefined,
      Extras: slice.primary.extras_description?.text ?? undefined,
    },
    orderCta: {
      text: slice.primary.order_cta_text ?? undefined,
      link: slice.primary.order_cta_link ?? undefined,
    },
    rules: items
      .map(mapRule)
      .filter((rule): rule is ChickenFingerCalculatorRule => Boolean(rule)),
  }

  return <ChickenFingerCalculator content={content} />
}

export default ChickenFingerCalculatorSlice

export const chickenFingerCalculatorFragment = graphql`
  fragment PageDataBodyChickenFingerCalculator on PrismicPageDataBodyChickenFingerCalculator {
    primary {
      anchor
      background_color
      eyebrow_text
      headline
      description {
        text
      }
      input_label
      minimum_people
      maximum_people
      slider_increment
      no_results_message
      tailgates_description {
        text
      }
      combos_description {
        text
      }
      extras_description {
        text
      }
      order_cta_text
      order_cta_link {
        ...BasicLinkFragment
      }
    }
    items {
      category
      item_name
      item_description {
        text
      }
      item_image {
        gatsbyImageData
        alt
      }
      applicable_people_min
      applicable_people_max
      serving_capacity
      quantity_mode
      fixed_quantity
      result_label
    }
  }
`
